'use client';
import React, { useCallback, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import Notification from '@/component/Notification';
import { LeadButtonProps } from '../../../app/utilities/interfaces';
import { acquireModalSession, releaseModalSession, subscribeModalSession } from '@/lib/client/modalSession';
import Link from 'next/link';
import { getLeadButtonTitle } from '@/lib/utils/leadButtonTitle';
import { hasSplashPopupBeenConsumedForSession } from '@/utils/leadTracking';
import LeadFormModal from '@/component/ui/Lead/LeadFormModal';
import { requestMobileLeadKeyboardForNextForm } from '@/component/ui/Lead/LeadForm';

const LEAD_MODAL_SESSION_ID = 'lead-form-modal';

const LeadButton: React.FC<LeadButtonProps> = ({ carId, carName, btnText, title, fullWidth, className, is_upcoming, trackingClass, is_support = false, formHeading, useSplashPopup, variantId, leadCTAHref, LeadPopupSubmitTracking, leadsumbittracking,datatestid, leadSource }) => {
    const [showModal, setShowModal] = useState(false);
    const [notification, setNotification] = useState<{
        message: string;
        isError: boolean;
        visible: boolean;
    }>({
        message: '',
        isError: false,
        visible: false,
    });

    const closeModal = useCallback(() => {
        setShowModal(false);
        releaseModalSession(LEAD_MODAL_SESSION_ID);
    }, []);

    const openModal = () => {
        if (useSplashPopup && !hasSplashPopupBeenConsumedForSession()) {
            window.dispatchEvent(new CustomEvent('open-splash-popup', { detail: { carId, variantId } }));
            return;
        }
        requestMobileLeadKeyboardForNextForm();
        flushSync(() => {
            acquireModalSession(LEAD_MODAL_SESSION_ID);
            setShowModal(true);
        });
    };

    useEffect(() => {
        const unsubscribe = subscribeModalSession((activeId) => {
            if (activeId !== LEAD_MODAL_SESSION_ID) {
                setShowModal(false);
            }
        });

        return unsubscribe;
    }, []);

    useEffect(() => {
        return () => releaseModalSession(LEAD_MODAL_SESSION_ID);
    }, []);

    // Central notification handler
    const showNotification = (message: string, isError = false) => {
        setNotification({ message, isError, visible: true });
        setTimeout(() => {
            setNotification((prev) => ({ ...prev, visible: false }));
        }, 3000);
    };

    const handleLeadSubmit = () => {
        closeModal();
    };

    const resolvedTitle = title ?? getLeadButtonTitle(carName, btnText);

    // if leadCTAHref is provided, redirect to that page
    if (leadCTAHref) {
        return (
            <Link
                title={resolvedTitle}
                href={leadCTAHref}
                className={
                    className
                        ? className
                        : `${fullWidth ? 'w-full' : 'w-1/2'} border-2 text-automotive-red card-btn font-semibold cursor-pointer transition duration-500 ease-in-out hover:text-white py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer ${trackingClass}`
                }
                aria-label={`Express interest in ${carName}`}
            >
                {btnText}
            </Link>
        );
    }

    return (
        <>
            <button
                onClick={openModal}
                title={resolvedTitle}

                className={
                    className
                        ? className
                        : `${fullWidth ? 'w-full' : 'w-1/2'} border-2 text-automotive-red card-btn font-semibold cursor-pointer transition duration-500 ease-in-out hover:text-white py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer ${trackingClass}`
                }
                aria-label={carName ? `${String(btnText)} for ${carName}` : String(btnText)}
                data-testid={datatestid}
            >
                {btnText}
            </button>

            <LeadFormModal
                showModal={showModal}
                carId={carId}
                variantId={variantId}
                carName={carName}
                onClose={closeModal}
                onSubmit={handleLeadSubmit}
                onError={(msg: string) => showNotification(msg, true)}
                formHeading={formHeading}
                is_upcoming={is_upcoming}
                is_support={is_support}
                LeadPopupSubmitTracking={LeadPopupSubmitTracking}
                leadsumbittracking={leadsumbittracking}
                leadSource={leadSource}
            />

            <Notification
                message={notification.message}
                isVisible={notification.visible}
                isError={notification.isError}
            />
        </>
    );
};

export default LeadButton;
